package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.milta.core.localization.LocalStrings
import com.milta.core.localization.OptimizationQuotaStrings
import com.milta.feature.optimizationquota.domain.usecase.GetOptimizationLimitsUseCase
import com.milta.feature.optimizationquota.domain.usecase.UpdateOptimizationLimitsUseCase
import com.milta.feature.optimizationquota.presentation.AdminUsageLimitsIntent
import com.milta.feature.optimizationquota.presentation.AdminUsageLimitsState
import com.milta.feature.optimizationquota.presentation.AdminUsageLimitsViewModel
import com.milta.feature.optimizationquota.presentation.OptimizationLimitField
import com.milta.feature.optimizationquota.presentation.OptimizationLimitFieldError
import com.milta.feature.optimizationquota.presentation.fillPlaceholders
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Text
import org.koin.compose.koinInject

/**
 * MIL-97 — A1, Admin "Limites d'usage": its own menu entry and page, organised in tabs. Only
 * "Optimisations RoutaR" is active (the 4 global limits, R4); "Tokens IA" is shown disabled with
 * "À venir" until the AI limit ticket. The existing Admin "Paramètres" page is unchanged.
 */
@Composable
fun AdminUsageLimitsContent() {
    val getLimits: GetOptimizationLimitsUseCase = koinInject()
    val updateLimits: UpdateOptimizationLimitsUseCase = koinInject()
    val viewModel = remember { AdminUsageLimitsViewModel(getLimits, updateLimits) }
    DisposableEffect(viewModel) { onDispose { viewModel.clear() } }
    val state by viewModel.state.collectAsState()
    LaunchedEffect(viewModel) { viewModel.onIntent(AdminUsageLimitsIntent.Load) }
    val strings = LocalStrings.current.optimizationQuota

    // MIL-98 — A1: the "Tokens IA" tab opens its own panel.
    var aiTab by remember { mutableStateOf(false) }
    Div(attrs = { style { property("max-width", if (aiTab) "720px" else "640px") } }) {
        Tabs(strings, aiTab) { aiTab = it }
        if (aiTab) {
            AdminAiCreditLimitsPanel()
            return@Div
        }
        Div(
            attrs = {
                style {
                    property("background", WebSurface); property("border", "1px solid $WebBorder")
                    property("border-radius", "12px"); padding(18.px)
                }
            },
        ) {
            Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "8px"); property("margin-bottom", "8px") } }) {
                Div(attrs = { style { property("font-size", "15px"); property("font-weight", "700"); property("color", WebTextPrimary) } }) {
                    Text(strings.tabOptimizations)
                }
            }
            Div(attrs = { style { property("font-size", "13px"); property("color", WebTextSecondary); property("line-height", "1.5"); property("margin-bottom", "16px") } }) {
                Text(strings.limitsDescription)
            }
            when {
                state.isLoading -> WebSpinner(size = 22, strokeWidth = 2)
                state.loadError != null -> Div(attrs = { style { property("color", WebErrorInk); property("font-size", "13px") } }) {
                    Text(if (state.loadError == "offline") strings.errorNoConnection else strings.errorGeneric)
                }
                else -> LimitsForm(state, strings, viewModel::onIntent)
            }
        }
    }
}

@Composable
private fun Tabs(strings: OptimizationQuotaStrings, aiTab: Boolean, onSelect: (aiTab: Boolean) -> Unit) {
    Div(attrs = { style { property("display", "flex"); property("gap", "6px"); property("margin-bottom", "14px"); property("border-bottom", "1px solid $WebBorder") } }) {
        Div(attrs = {
            onClick { onSelect(false) }
            style { tabStyle(this, selected = !aiTab) }
        }) { Text(strings.tabOptimizations) }
        // MIL-98 — "Tokens IA" is active (no more "À venir"), without any "Nouveau" badge.
        Div(attrs = {
            onClick { onSelect(true) }
            style {
                tabStyle(this, selected = aiTab)
                property("display", "flex"); property("gap", "6px"); property("align-items", "center")
            }
        }) {
            Text(strings.tabAiTokens)
        }
    }
}

private fun tabStyle(builder: org.jetbrains.compose.web.css.StyleScope, selected: Boolean) = with(builder) {
    padding(9.px, 14.px); property("font-size", "13.5px"); property("cursor", "pointer"); property("margin-bottom", "-1px")
    property("font-weight", if (selected) "700" else "500")
    property("color", if (selected) WebPrimary else WebTextSecondary)
    property("border-bottom", if (selected) "2px solid $WebTealAccent" else "2px solid transparent")
}

@Composable
private fun LimitsForm(state: AdminUsageLimitsState, strings: OptimizationQuotaStrings, onIntent: (AdminUsageLimitsIntent) -> Unit) {
    val saved = state.savedLimits
    if (saved != null) {
        Div(attrs = { style { property("margin-bottom", "4px") } }) {
            InlineBanner(
                kind = InlineBannerKind.Success,
                text = strings.limitsSaved.fillPlaceholders(
                    "enterpriseTrial" to saved.enterpriseTrial,
                    "enterprisePaid" to saved.enterprisePaid,
                    "driverTrial" to saved.driverTrial,
                    "driverPaid" to saved.driverPaid,
                ),
            )
        }
    }
    Div(attrs = { style { property("display", "grid"); property("grid-template-columns", "1.4fr 1fr 1fr"); property("gap", "10px 12px"); property("align-items", "start") } }) {
        Div {}
        ColumnLabel(strings.columnTrial)
        ColumnLabel(strings.columnPaid)

        RowLabel(strings.rowEnterprise)
        LimitInput(state, strings, OptimizationLimitField.EnterpriseTrial, onIntent)
        LimitInput(state, strings, OptimizationLimitField.EnterprisePaid, onIntent)

        RowLabel(strings.rowDriver)
        LimitInput(state, strings, OptimizationLimitField.DriverTrial, onIntent)
        LimitInput(state, strings, OptimizationLimitField.DriverPaid, onIntent)
    }
    Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary); property("margin", "14px 0"); property("line-height", "1.5") } }) {
        Text(strings.limitsFootnote)
    }
    val saveError = state.saveError
    if (saveError != null) {
        Div(attrs = { style { property("font-size", "12.5px"); property("color", WebErrorInk); property("margin-bottom", "10px") } }) {
            Text(
                when (saveError) {
                    "validation" -> strings.limitsRejected
                    "offline" -> strings.errorNoConnection
                    else -> strings.errorGeneric
                },
            )
        }
    }
    Button(
        attrs = {
            if (!state.isSaving) onClick { onIntent(AdminUsageLimitsIntent.Save) }
            style {
                gradientButtonStyle(this)
                if (state.isSaving) {
                    property("opacity", "0.5"); property("cursor", "not-allowed"); property("box-shadow", "none")
                }
            }
        },
    ) {
        if (state.isSaving) WebSpinner(size = 16, strokeWidth = 2, color = "#FFFFFF", trackColor = "rgba(255,255,255,0.35)")
        else Text(strings.saveAction)
    }
}

@Composable
private fun ColumnLabel(label: String) {
    Div(attrs = {
        style {
            property("font-size", "11px"); property("font-weight", "700"); property("color", WebTextSecondary)
            property("text-transform", "uppercase"); property("letter-spacing", "0.05em")
        }
    }) { Text(label) }
}

@Composable
private fun RowLabel(label: String) {
    Div(attrs = { style { property("font-size", "13.5px"); property("font-weight", "600"); property("color", WebTextPrimary); property("padding-top", "9px") } }) {
        Text(label)
    }
}

@Composable
private fun LimitInput(
    state: AdminUsageLimitsState,
    strings: OptimizationQuotaStrings,
    field: OptimizationLimitField,
    onIntent: (AdminUsageLimitsIntent) -> Unit,
) {
    val error = state.fieldErrors[field]
    Div {
        Input(
            type = InputType.Text,
            attrs = {
                attr("inputmode", "numeric")
                value(state.values[field].orEmpty())
                onInput { event -> onIntent(AdminUsageLimitsIntent.UpdateField(field, event.value)) }
                style {
                    property("width", "100%"); property("box-sizing", "border-box"); padding(8.px, 10.px)
                    property("border-radius", "8px"); property("font-size", "14px"); property("color", WebTextPrimary)
                    property("border", "1.5px solid " + if (error != null) WebError else WebBorder)
                }
            },
        )
        if (error != null) {
            Div(attrs = { style { property("font-size", "11.5px"); property("color", WebErrorInk); property("margin-top", "4px") } }) {
                Text(
                    when (error) {
                        OptimizationLimitFieldError.Required -> strings.fieldRequired
                        OptimizationLimitFieldError.MustBePositive -> strings.fieldMustBePositive
                    },
                )
            }
        }
    }
}
